import React, { useEffect, useState } from 'react';
import { ArrowLeft, Plus, Save } from 'lucide-react';
import { Card, Btn, Input, Field, Select, Table, Spinner, ErrBox } from '../../../components/UI';
import { contractAPI } from '../../../utils/api';
import { fmtRp, fmtDate } from '../../../utils/format';

const statusCfg = {
  DRAFT: { bg: '#F3F4F6', color: '#374151' },
  AKTIF: { bg: '#DCFCE7', color: '#166534' },
  DALAM_PELAKSANAAN: { bg: '#DBEAFE', color: '#1E40AF' },
  SELESAI: { bg: '#ECFDF5', color: '#059669' },
  EXPIRED: { bg: '#FEE2E2', color: '#991B1B' },
  DIBATALKAN: { bg: '#FEF2F2', color: '#DC2626' },
};

const levelBadge = (val, color) => val ? (
  <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 12, fontSize: 11, fontWeight: 600, background: `${color}22`, color, border: `1px solid ${color}44` }}>{val}</span>
) : null;
const ProgramBadge = ({v}) => levelBadge(v, "#7C3AED");
const KegiatanBadge = ({v}) => levelBadge(v, "#D97706");
const KroBadge = ({v}) => levelBadge(v, "#059669");

export default function KontrakDetailTab({ id, onBack, onEditKontrak }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [showAddendum, setShowAddendum] = useState(false);
  const [showMilestone, setShowMilestone] = useState(false);
  const [addForm, setAddForm] = useState({ deskripsi: '', perubahan_nilai: '', tanggal_efektif: '' });
  const [msForm, setMsForm] = useState({ deskripsi: '', jumlah_cair: '', tanggal_cair: '' });

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const r = await contractAPI.getById(id);
      setData(r.data);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [id]);

  const handleStatus = async (status) => {
    if (!window.confirm(`Ubah status ke "${status}"?`)) return;
    try {
      await contractAPI.updateStatus(id, { status });
      load();
    } catch (e) { alert(e.message); }
  };

  const handleAddAddendum = async () => {
    if (!addForm.deskripsi) return alert('Deskripsi wajib diisi');
    setSaving(true);
    try {
      await contractAPI.addAddendum(id, addForm);
      setShowAddendum(false);
      setAddForm({ deskripsi: '', perubahan_nilai: '', tanggal_efektif: '' });
      load();
    } catch (e) { alert(e.message); }
    finally { setSaving(false); }
  };

  const handleAddMilestone = async () => {
    if (!msForm.jumlah_cair) return alert('Jumlah cair wajib diisi');
    setSaving(true);
    try {
      await contractAPI.addMilestone(id, msForm);
      setShowMilestone(false);
      setMsForm({ deskripsi: '', jumlah_cair: '', tanggal_cair: '' });
      load();
    } catch (e) { alert(e.message); }
    finally { setSaving(false); }
  };

  if (loading) return <Spinner />;
  if (error) return <ErrBox msg={error} onRetry={load} />;
  if (!data) return null;

  const c = statusCfg[data.status_kontrak] || { bg: '#F3F4F6', color: '#374151' };

  const addendumCols = [
    { key: 'addendum_no', label: 'No. Addendum', render: (_, r) => <span style={{ fontWeight: 600, fontSize: 13 }}>{r.addendum_no}</span> },
    { key: 'tanggal', label: 'Tanggal', render: (_, r) => fmtDate(r.tanggal) },
    { key: 'deskripsi', label: 'Deskripsi', render: (_, r) => r.deskripsi },
    { key: 'perubahan_nilai', label: 'Perubahan Nilai', render: (_, r) => r.perubahan_nilai ? fmtRp(Number(r.perubahan_nilai)) : '—' },
    { key: 'tanggal_efektif', label: 'Efektif', render: (_, r) => fmtDate(r.tanggal_efektif) || '—' },
  ];

  const milestoneCols = [
    { key: 'milestone_no', label: 'No. Milestone', render: (_, r) => <span style={{ fontWeight: 600, fontSize: 13 }}>{r.milestone_no}</span> },
    { key: 'deskripsi', label: 'Deskripsi', render: (_, r) => r.deskripsi || '—' },
    { key: 'jumlah_cair', label: 'Jumlah Cair', render: (_, r) => <span style={{ color: '#059669', fontWeight: 600 }}>{fmtRp(Number(r.jumlah_cair) || 0)}</span> },
    { key: 'tanggal_cair', label: 'Tanggal Cair', render: (_, r) => fmtDate(r.tanggal_cair) },
    { key: 'no_spp', label: 'No. SPP', render: (_, r) => r.no_spp || '—' },
    { key: 'status', label: 'Status', render: (_, r) => {
      const s = { DISETUJUI: { bg: '#DCFCE7', color: '#166534' }, DICAIRKAN: { bg: '#DBEAFE', color: '#1E40AF' }, DITOLAK: { bg: '#FEE2E2', color: '#991B1B' } };
      const sc = s[r.status] || { bg: '#F3F4F6', color: '#374151' };
      return <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: sc.bg, color: sc.color }}>{r.status || 'PENDING'}</span>;
    }},
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', gap: 8 }}>
        <Btn variant="secondary" onClick={onBack}><ArrowLeft size={14} /> Kembali</Btn>
        <Btn variant="secondary" onClick={() => onEditKontrak(id)}>Edit Kontrak</Btn>
      </div>

      <Card style={{ padding: 24, marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{data.contract_no}</h3>
              <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, background: c.bg, color: c.color }}>{data.status_kontrak}</span>
            </div>
            <p style={{ margin: 0, fontSize: 15, color: '#374151' }}>{data.judul}</p>
            {data.deskripsi && <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6B7280' }}>{data.deskripsi}</p>}
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {['DRAFT', 'AKTIF', 'DALAM_PELAKSANAAN', 'SELESAI', 'EXPIRED', 'DIBATALKAN'].map(s => (
              <button key={s} onClick={() => handleStatus(s)} style={{ padding: '4px 10px', border: '1px solid #E5E7EB', borderRadius: 6, background: data.status_kontrak === s ? c.bg : 'white', color: data.status_kontrak === s ? c.color : '#374151', cursor: 'pointer', fontSize: 12, fontWeight: 500 }}>{s}</button>
            ))}
          </div>
        </div>
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <Card style={{ padding: 20 }}>
          <h4 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600 }}>Informasi Kontrak</h4>
          <table style={{ width: '100%', fontSize: 13 }}>
            <tbody>
              <tr><td style={{ color: '#6B7280', width: 140 }}>Vendor</td><td><strong>{data.vendor_nama}</strong> ({data.vendor_kode})</td></tr>
              <tr><td style={{ color: '#6B7280' }}>Tipe</td><td>{data.tipe_kontrak}</td></tr>
              <tr><td style={{ color: '#6B7280' }}>Nilai</td><td><strong>{fmtRp(Number(data.nilai_kontrak) || 0)}</strong> {data.mata_uang}</td></tr>
              <tr><td style={{ color: '#6B7280' }}>Terpakai</td><td style={{ color: '#059669' }}>{fmtRp(Number(data.nilai_kontrak_terpakai) || 0)}</td></tr>
              <tr><td style={{ color: '#6B7280' }}>Sisa</td><td style={{ color: '#D97706' }}>{fmtRp(Number(data.nilai_kontrak - data.nilai_kontrak_terpakai) || 0)}</td></tr>
              <tr><td style={{ color: '#6B7280' }}>Mulai</td><td>{fmtDate(data.tanggal_mulai)}</td></tr>
              <tr><td style={{ color: '#6B7280' }}>Selesai</td><td>{fmtDate(data.tanggal_selesai)}</td></tr>
              {data.penanggung_jawab && <tr><td style={{ color: '#6B7280' }}>PJ</td><td>{data.penanggung_jawab}</td></tr>}
              {data.no_dokumen && <tr><td style={{ color: '#6B7280' }}>No. Dokumen</td><td>{data.no_dokumen}</td></tr>}
              {data.sumber_dana_nama && <tr><td style={{ color: '#6B7280' }}>Sumber Dana</td><td>{data.sumber_dana_nama}</td></tr>}
            </tbody>
          </table>
        </Card>

        <Card style={{ padding: 20 }}>
          <h4 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600 }}>COA Hirarki</h4>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
            {data.program_kode && <ProgramBadge v={`${data.program_kode} - ${data.program_nama}`} />}
            {data.kegiatan_kode && <KegiatanBadge v={`${data.kegiatan_kode} - ${data.kegiatan_nama}`} />}
            {data.kro_kode && <KroBadge v={`${data.kro_kode} - ${data.kro_nama}`} />}
            {data.akun_kode && <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: '#DBEAFE', color: '#1E3A8A' }}>{data.akun_kode} - {data.akun_nama}</span>}
          </div>
        </Card>
      </div>

      <Card style={{ padding: 20, marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h4 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>Addendum ({data.addendum?.length || 0})</h4>
          <Btn variant="secondary" onClick={() => setShowAddendum(!showAddendum)}>+ Addendum</Btn>
        </div>
        {showAddendum && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: 12, marginBottom: 16, padding: 16, background: '#F9FAFB', borderRadius: 8 }}>
            <Input value={addForm.deskripsi} onChange={e => setAddForm({ ...addForm, deskripsi: e.target.value })} placeholder="Deskripsi perubahan" />
            <Input value={addForm.perubahan_nilai} onChange={e => setAddForm({ ...addForm, perubahan_nilai: e.target.value })} placeholder="Perubahan nilai (positif/negatif)" type="number" />
            <Input value={addForm.tanggal_efektif} onChange={e => setAddForm({ ...addForm, tanggal_efektif: e.target.value })} type="date" />
            <Btn onClick={handleAddAddendum} disabled={saving}><Save size={13} /> Simpan</Btn>
          </div>
        )}
        {data.addendum?.length > 0 ? <Table cols={addendumCols} data={data.addendum} /> : <p style={{ color: '#9CA3AF', fontSize: 13 }}>Belum ada addendum</p>}
      </Card>

      <Card style={{ padding: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h4 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>Milestone / Pencapaian ({data.milestones?.length || 0})</h4>
          <Btn variant="secondary" onClick={() => setShowMilestone(!showMilestone)}>+ Milestone</Btn>
        </div>
        {showMilestone && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: 12, marginBottom: 16, padding: 16, background: '#F9FAFB', borderRadius: 8 }}>
            <Input value={msForm.deskripsi} onChange={e => setMsForm({ ...msForm, deskripsi: e.target.value })} placeholder="Deskripsi milestone" />
            <Input value={msForm.jumlah_cair} onChange={e => setMsForm({ ...msForm, jumlah_cair: e.target.value })} placeholder="Jumlah cair" type="number" />
            <Input value={msForm.tanggal_cair} onChange={e => setMsForm({ ...msForm, tanggal_cair: e.target.value })} type="date" />
            <Btn onClick={handleAddMilestone} disabled={saving}><Save size={13} /> Simpan</Btn>
          </div>
        )}
        {data.milestones?.length > 0 ? <Table cols={milestoneCols} data={data.milestones} /> : <p style={{ color: '#9CA3AF', fontSize: 13 }}>Belum ada milestone</p>}
      </Card>
    </div>
  );
}
